import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Send as SendIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  query,
  orderBy,
  limit,
  where,
  collectionGroup,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../config/firebase';

interface ScheduledNotificationConfig {
  enabled: boolean;
  title: string;
  message: string;
  dayOfMonth?: number;
}

const DAY_OF_MONTH_KEYS = ['monthlyRecipeReminder'];

interface ScheduledNotificationsDoc {
  [key: string]: ScheduledNotificationConfig;
}

interface NotificationHistoryItem {
  id: string;
  title: string;
  message: string;
  userId: string;
  createdAt: any;
}

const SCHEDULED_NOTIFICATIONS_META: {
  key: string;
  name: string;
  schedule: string;
}[] = [
  { key: 'weeklyEventReminder', name: 'Lembrete semanal (Quartas)', schedule: 'Quartas 10h' },
  { key: 'biweeklyInventoryReminder', name: 'Lembrete quinzenal (Acervo)', schedule: 'Dia 1 e 15, 10h' },
  { key: 'twoDaysBeforeEvent', name: '2 dias antes do evento', schedule: 'Diario 9h' },
  { key: 'oneDayBeforeEvent', name: '1 dia antes do evento', schedule: 'Diario 9h' },
  { key: 'holidayReminder', name: 'Lembrete de feriados', schedule: 'Segundas 10h' },
  { key: 'monthlyRecipeReminder', name: 'Lembrete mensal (Receitas)', schedule: 'Dia 1, 14h' },
];

const DEFAULT_CONFIGS: ScheduledNotificationsDoc = {
  weeklyEventReminder: { enabled: true, title: 'Fim de semana chegando!', message: 'Vai receber no fim de semana? Nao esqueca de montar seu evento.' },
  biweeklyInventoryReminder: { enabled: true, title: 'Atualize seu acervo', message: 'Nao esqueca de manter seu acervo atualizado!' },
  twoDaysBeforeEvent: { enabled: true, title: 'Evento em 2 dias!', message: 'Ja olhou sua lista de tarefas? Cuidado para nao esquecer nada!' },
  oneDayBeforeEvent: { enabled: true, title: 'Evento amanha!', message: 'Esta chegando! Amanha tem evento. Ja conferiu se seu lavabo esta limpinho?' },
  holidayReminder: { enabled: true, title: 'Feriado chegando!', message: 'Oba! Feriado chegando! Que tal receber os amigos em casa?' },
  monthlyRecipeReminder: { enabled: true, title: 'Hora de cozinhar!', message: 'Tem receitinha nova? Nao esquece de acrescentar no seu livro de receitas.' },
};

export const AdminScheduledNotifications: React.FC = () => {
  const { theme } = useTheme();

  // Scheduled notifications config
  const [configs, setConfigs] = useState<ScheduledNotificationsDoc>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ title: '', message: '', dayOfMonth: '1' });

  // Manual push dialog
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ title: '', message: '', target: 'all', email: '' });
  const [sendingManual, setSendingManual] = useState(false);

  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<NotificationHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, 'config', 'scheduledNotifications');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setConfigs(docSnap.data() as ScheduledNotificationsDoc);
      } else {
        // Create default config if it doesn't exist
        await setDoc(docRef, DEFAULT_CONFIGS);
        setConfigs(DEFAULT_CONFIGS);
      }
    } catch (err) {
      console.error('Erro ao carregar configuracoes:', err);
      setError('Erro ao carregar configuracoes de notificacoes');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const q = query(
        collection(db, 'notifications'),
        where('type', '==', 'general'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);
      const items: NotificationHistoryItem[] = snapshot.docs.map((d) => ({
        id: d.id,
        title: d.data().title || '',
        message: d.data().message || '',
        userId: d.data().userId || '',
        createdAt: d.data().createdAt,
      }));
      setHistory(items);
    } catch (err) {
      console.error('Erro ao carregar historico:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleToggle = async (key: string, enabled: boolean) => {
    try {
      const updatedConfigs = {
        ...configs,
        [key]: { ...configs[key], enabled },
      };
      const docRef = doc(db, 'config', 'scheduledNotifications');
      await setDoc(docRef, updatedConfigs);
      setConfigs(updatedConfigs);
      setSuccess(`Notificacao "${SCHEDULED_NOTIFICATIONS_META.find((m) => m.key === key)?.name}" ${enabled ? 'ativada' : 'desativada'}`);
    } catch (err) {
      console.error('Erro ao atualizar toggle:', err);
      setError('Erro ao atualizar configuracao');
    }
  };

  const handleEditOpen = (key: string) => {
    setEditingKey(key);
    setEditForm({
      title: configs[key]?.title || '',
      message: configs[key]?.message || '',
      dayOfMonth: String(configs[key]?.dayOfMonth || 1),
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingKey) return;
    try {
      const entry: ScheduledNotificationConfig = {
        ...configs[editingKey],
        title: editForm.title,
        message: editForm.message,
      };
      if (DAY_OF_MONTH_KEYS.includes(editingKey)) {
        const parsed = parseInt(editForm.dayOfMonth, 10);
        entry.dayOfMonth = Math.min(28, Math.max(1, Number.isNaN(parsed) ? 1 : parsed));
      }
      const updatedConfigs = {
        ...configs,
        [editingKey]: entry,
      };
      const docRef = doc(db, 'config', 'scheduledNotifications');
      await setDoc(docRef, updatedConfigs);
      setConfigs(updatedConfigs);
      setSuccess('Mensagem atualizada com sucesso!');
      setEditDialogOpen(false);
      setEditingKey(null);
    } catch (err) {
      console.error('Erro ao salvar mensagem:', err);
      setError('Erro ao salvar mensagem');
    }
  };

  const handleSendManual = async () => {
    if (!manualForm.title || !manualForm.message) {
      setError('Preencha titulo e mensagem');
      return;
    }

    try {
      setSendingManual(true);
      setError(null);

      if (manualForm.target === 'specific') {
        if (!manualForm.email) {
          setError('Informe o email do usuario');
          setSendingManual(false);
          return;
        }

        // Find user by email
        const usersQuery = query(
          collection(db, 'users'),
          where('email', '==', manualForm.email.trim())
        );
        const usersSnap = await getDocs(usersQuery);

        if (usersSnap.empty) {
          setError('Usuario nao encontrado com esse email');
          setSendingManual(false);
          return;
        }

        const userId = usersSnap.docs[0].id;

        // Create single notification
        const batch = writeBatch(db);
        const notifRef = doc(collection(db, 'notifications'));
        batch.set(notifRef, {
          userId,
          type: 'general',
          title: manualForm.title,
          message: manualForm.message,
          read: false,
          createdAt: Timestamp.now(),
          sentBy: 'admin',
        });
        await batch.commit();

        setSuccess(`Notificacao enviada para ${manualForm.email}`);
      } else {
        const usersSnap = await getDocs(collection(db, 'users'));
        let userIds = usersSnap.docs.map((d) => d.id);

        if (['free', 'basic', 'pro'].includes(manualForm.target)) {
          const subsSnap = await getDocs(collectionGroup(db, 'subscription'));
          const planByUser: Record<string, string> = {};
          subsSnap.forEach((d) => {
            if (d.id !== 'current') return;
            const parentId = d.ref.parent.parent?.id;
            if (!parentId) return;
            const data = d.data();
            const raw = String(data.plan || 'free').toLowerCase();
            const active = data.status === 'active' || data.status === 'trialing';
            const plan = !active || raw === 'free' ? 'free' : raw === 'pro' ? 'pro' : 'basic';
            planByUser[parentId] = plan;
          });
          userIds = userIds.filter((id) => (planByUser[id] || 'free') === manualForm.target);
          if (userIds.length === 0) {
            setError('Nenhum usuario nesse plano.');
            setSendingManual(false);
            return;
          }
        }

        // Batch writes in groups of 500 (Firestore limit)
        let batchCount = 0;
        let batch = writeBatch(db);

        for (const userId of userIds) {
          const notifRef = doc(collection(db, 'notifications'));
          batch.set(notifRef, {
            userId,
            type: 'general',
            title: manualForm.title,
            message: manualForm.message,
            read: false,
            createdAt: Timestamp.now(),
            sentBy: 'admin',
          });
          batchCount++;

          if (batchCount === 500) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }

        if (batchCount > 0) {
          await batch.commit();
        }

        setSuccess(`Notificacao enviada para ${userIds.length} usuarios!`);
      }

      setManualDialogOpen(false);
      setManualForm({ title: '', message: '', target: 'all', email: '' });
    } catch (err) {
      console.error('Erro ao enviar notificacao manual:', err);
      setError('Erro ao enviar notificacao');
    } finally {
      setSendingManual(false);
    }
  };

  const scheduleLabel = (key: string): string => {
    const meta = SCHEDULED_NOTIFICATIONS_META.find((m) => m.key === key);
    if (key === 'monthlyRecipeReminder') {
      return `Dia ${configs[key]?.dayOfMonth || 1}, 14h`;
    }
    return meta?.schedule || '';
  };

  const handleHistoryToggle = () => {
    if (!historyOpen) {
      loadHistory();
    }
    setHistoryOpen(!historyOpen);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Carregando" sx={{ color: theme.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
          Notificacoes Agendadas
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton
            aria-label="Recarregar notificações"
            onClick={loadConfig}
            sx={{
              color: theme.colors.primary,
              '&:hover': { backgroundColor: `${theme.colors.primary}15` },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setManualDialogOpen(true)}
            sx={{ backgroundColor: theme.colors.primary, color: 'white' }}
          >
            Enviar Push Manual
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Scheduled Notifications List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {SCHEDULED_NOTIFICATIONS_META.map((meta) => {
          const config = configs[meta.key];
          if (!config) return null;

          return (
            <Card
              key={meta.key}
              sx={{
                backgroundColor: theme.colors.card,
                borderRadius: theme.radii.medium,
                border: `1px solid ${theme.colors.border}`,
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                  boxShadow: `0 2px 8px ${theme.colors.primary}20`,
                },
              }}
              onClick={() => handleEditOpen(meta.key)}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                      {meta.name}
                    </Typography>
                    <Chip
                      label={scheduleLabel(meta.key)}
                      size="small"
                      sx={{
                        backgroundColor: `${theme.colors.primary}20`,
                        color: theme.colors.primary,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                    <Chip
                      label={config.enabled ? 'Ativa' : 'Inativa'}
                      size="small"
                      sx={{
                        backgroundColor: config.enabled ? '#4caf5020' : '#f4433620',
                        color: config.enabled ? '#4caf50' : '#f44336',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
                    {config.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
                    {config.message}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    aria-label={`Editar ${meta.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditOpen(meta.key);
                    }}
                    sx={{ color: theme.colors.primary }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <Switch
                    checked={config.enabled}
                    inputProps={{ 'aria-label': `Ativar ${meta.name}` }}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggle(meta.key, e.target.checked);
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: theme.colors.primary,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: theme.colors.primary,
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* History Section */}
      <Box sx={{ mb: 3 }}>
        <Button
          onClick={handleHistoryToggle}
          endIcon={historyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            color: theme.colors.text.primary,
            textTransform: 'none',
            fontWeight: 'bold',
            fontSize: '1rem',
            mb: 1,
          }}
        >
          Historico de Notificacoes Recentes
        </Button>
        <Collapse in={historyOpen}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress aria-label="Carregando" size={24} sx={{ color: theme.colors.primary }} />
            </Box>
          ) : history.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, py: 2 }}>
              Nenhuma notificacao geral encontrada.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: theme.colors.card,
                borderRadius: theme.radii.medium,
                border: `1px solid ${theme.colors.border}`,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Titulo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Mensagem</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {item.userId.substring(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(item.createdAt)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </Box>

      {/* Edit Dialog */}
      <Dialog aria-labelledby="adminschedulednotifications-dlg1-title" open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle id="adminschedulednotifications-dlg1-title" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Editar Notificacao</Typography>
          {editingKey && (
            <Typography variant="body2" color="text.secondary">
              {SCHEDULED_NOTIFICATIONS_META.find((m) => m.key === editingKey)?.name} - {scheduleLabel(editingKey)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Titulo
            </Typography>
            <TextField
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              fullWidth
              placeholder="Titulo da notificacao"
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Mensagem
            </Typography>
            <TextField
              value={editForm.message}
              onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Mensagem da notificacao"
            />
          </Box>
          {editingKey && DAY_OF_MONTH_KEYS.includes(editingKey) && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Dia do mes
              </Typography>
              <TextField
                type="number"
                value={editForm.dayOfMonth}
                onChange={(e) => setEditForm({ ...editForm, dayOfMonth: e.target.value })}
                fullWidth
                inputProps={{ min: 1, max: 28 }}
                helperText="Dia em que a notificacao e enviada (1 a 28), sempre as 14h."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">Cancelar</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={!editForm.title || !editForm.message}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Push Dialog */}
      <Dialog aria-labelledby="adminschedulednotifications-dlg2-title" open={manualDialogOpen} onClose={() => setManualDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle id="adminschedulednotifications-dlg2-title" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Enviar Push Manual</Typography>
          <Typography variant="body2" color="text.secondary">
            Envia uma notificacao push para usuarios selecionados
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Titulo *
            </Typography>
            <TextField
              value={manualForm.title}
              onChange={(e) => setManualForm({ ...manualForm, title: e.target.value })}
              fullWidth
              placeholder="Titulo da notificacao"
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Mensagem *
            </Typography>
            <TextField
              value={manualForm.message}
              onChange={(e) => setManualForm({ ...manualForm, message: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Mensagem da notificacao"
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Destinatario
            </Typography>
            <RadioGroup
              value={manualForm.target}
              onChange={(e) => setManualForm({ ...manualForm, target: e.target.value })}
            >
              <FormControlLabel value="all" control={<Radio />} label="Todos os usuarios" />
              <FormControlLabel value="free" control={<Radio />} label="Somente plano Gratis (nao compraram)" />
              <FormControlLabel value="basic" control={<Radio />} label="Somente plano Basic" />
              <FormControlLabel value="pro" control={<Radio />} label="Somente plano Pro" />
              <FormControlLabel value="specific" control={<Radio />} label="Usuario especifico" />
            </RadioGroup>
          </Box>
          {manualForm.target === 'specific' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Email do usuario
              </Typography>
              <TextField
                value={manualForm.email}
                onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                fullWidth
                placeholder="email@exemplo.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.colors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
          {['all', 'free', 'basic', 'pro'].includes(manualForm.target) && (
            <Alert severity="warning">
              {manualForm.target === 'all'
                ? 'Essa acao enviara uma notificacao para TODOS os usuarios cadastrados.'
                : `Essa acao enviara uma notificacao para todos os usuarios do plano ${manualForm.target === 'free' ? 'Gratis' : manualForm.target === 'basic' ? 'Basic' : 'Pro'}.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setManualDialogOpen(false)} color="inherit">Cancelar</Button>
          <Button
            onClick={handleSendManual}
            variant="contained"
            startIcon={sendingManual ? <CircularProgress aria-label="Carregando" size={16} color="inherit" /> : <SendIcon />}
            disabled={sendingManual || !manualForm.title || !manualForm.message}
          >
            {sendingManual ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
